import { Badge, Col, Form, InputGroup, Row, ListGroup } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import moment from 'moment'
import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'
import { useAuth, globalAdministratorRole } from '../../auth'
import { AdminExportButton, ButtonModals, DismissibleAlert } from '../../common'
import type { AlertState } from '../../common/alert/types'
import type { StatusValue } from '../../common'
import { selectLayoutableTitleWithMargin } from '../articleSelectors'
import { LayoutableStatusSpinner } from './LayoutableStatusSpinner'
import { getArticleOccupyingLines } from './articleListFunctions'
import type { Article, Layoutable } from '../types'
moment.locale('ja')

type BaseLayoutable = Layoutable & {
  status?: StatusValue | null
  id?: string
  lockVersion?: number | null
  assignee?: { name?: string }
  bottomAdNumberOfColumns?: number
  templateFilename?: string
}

type PageLayoutable = BaseLayoutable & {
  layoutableType: 'Page'
}

type SubLayoutLayoutable = BaseLayoutable & {
  layoutableType: 'SubLayout'
  issueDate?: string
  media?: { name?: string }
  name?: string
  draftNumber?: string | number
}

type SpinnerLayoutable = PageLayoutable | SubLayoutLayoutable

type SpinnerProps = {
  delay: number
  waitedTime: number
  setWaitedTime: (time: number) => void
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>
  cancelInProgress: boolean
  setCancelInProgress: (v: boolean) => void
}

type ValidationMessage = { message: string }

export const LayoutValidationErrorsAlert = ({
  closeValidationErrors,
  errors
}: {
  closeValidationErrors: () => void
  errors: ValidationMessage[]
}) => {
  if (errors.length > 0) {
    const title = '割り付け実行前に以下の確認をしてください'
    const alertBody = (
      <ol>
        {errors.map(({ message }, idx) => (
          <li key={`layout-validation-error-${idx}`}>{message}</li>
        ))}
      </ol>
    )
    const alert: AlertState = {
      show: true,
      title,
      variant: 'danger',
      alertBody
    }
    return <DismissibleAlert className='layout-validation-errors-alert' alert={alert} onClose={closeValidationErrors} />
  }
}

export const LayoutValidationWarningsAlert = ({
  closeValidationWarnings,
  warnings
}: {
  closeValidationWarnings: () => void
  warnings: ValidationMessage[]
}) => {
  if (warnings.length > 0) {
    const title = '以下の点にご注意ください'
    const alertBody = (
      <ol>
        {warnings.map(({ message }, idx) => (
          <li key={`layout-validation-warning-${idx}`}>{message}</li>
        ))}
      </ol>
    )
    const alert: AlertState = {
      show: true,
      title,
      variant: 'warning',
      alertBody
    }
    return (
      <DismissibleAlert className='layout-validation-warnings-alert' alert={alert} onClose={closeValidationWarnings} />
    )
  }
}

type ArticleListHeaderProps = {
  layoutable: SpinnerLayoutable
  spinnerProps: SpinnerProps
  enumValues: { statuses: Record<string, string> }
  openedModal: string | null
  [key: string]: unknown
}

export const ArticleListHeader = ({
  layoutable,
  layoutableType,
  spinnerProps,
  enumValues,
  ...props
}: ArticleListHeaderProps) => {
  const isGlobalAdministratorRole = globalAdministratorRole(useAuth())
  const titleWithMargin = useSelector(selectLayoutableTitleWithMargin)
  const adminExportButton = isGlobalAdministratorRole ? (
    <Col sm='auto'>
      <AdminExportButton />
    </Col>
  ) : undefined
  const layoutableTypeBadge =
    layoutableType === 'Page' ? (
      <Badge bg='primary' className='fs-6'>
        紙面
      </Badge>
    ) : (
      <Badge bg='info' className='fs-6'>
        小組
      </Badge>
    )
  const nonFoldingLayoutBadge = layoutable.nonFoldingLayout ? (
    <Badge bg='success' className='fs-6'>
      総流し
    </Badge>
  ) : null
  switch (layoutable.layoutableType) {
    case 'Page': {
      const page = layoutable
      return (
        <Row className='align-items-center'>
          <Col className='fs-3 mr-auto layoutableType pageName d-flex align-items-center gap-2'>
            {layoutableTypeBadge} {titleWithMargin} {nonFoldingLayoutBadge}
          </Col>
          <Col sm='auto' className='assigneeName fs-5'>
            担当者: {page?.assignee?.name}
          </Col>
          {adminExportButton}
          <LayoutableStatusSpinner
            layoutable={layoutable}
            spinnerProps={spinnerProps}
            enumValues={enumValues}
            openedModal={props.openedModal}
          />
          <Col sm='auto' className='justify-content-end ps-1'>
            <ButtonModals {...(props as Parameters<typeof ButtonModals>[0])} />
          </Col>
        </Row>
      )
    }
    case 'SubLayout': {
      const subLayout = layoutable
      const issueDate = moment(subLayout.issueDate).format('LL')
      const subLayoutDisplayName = `${subLayout?.media?.name} ${issueDate} ${subLayout?.name} (${subLayout?.draftNumber})`
      return (
        <Row className='align-items-center'>
          <Col className='fs-3 mr-auto layoutableType subLayoutName d-flex align-items-center gap-2'>
            {layoutableTypeBadge} {subLayoutDisplayName}
          </Col>
          <Col sm='auto' className='assigneeName fs-5'>
            担当者: {subLayout?.assignee?.name}
          </Col>
          {adminExportButton}
          <LayoutableStatusSpinner
            layoutable={layoutable}
            spinnerProps={spinnerProps}
            enumValues={enumValues}
            openedModal={props.openedModal}
          />
          <Col sm='auto' className='justify-content-end ps-1'>
            <ButtonModals {...(props as Parameters<typeof ButtonModals>[0])} />
          </Col>
        </Row>
      )
    }
  }
}

export const PageInformationFooter = ({
  layoutable
}: {
  layoutable: { bottomAdNumberOfColumns?: number; templateFilename?: string }
}) => {
  const page = layoutable
  return (
    <Row xs='auto' className='pb-3'>
      <Col sm={3}>
        <InputGroup size='sm'>
          <InputGroup.Text>記事下広告分の余白</InputGroup.Text>
          <Form.Control
            id='bottomAdColumnsInput'
            type='number'
            name='bottomAdNumberOfColumns'
            defaultValue={page?.bottomAdNumberOfColumns}
            readOnly
          />
          <InputGroup.Text>段</InputGroup.Text>
        </InputGroup>
      </Col>
      <Col sm={4}>
        <InputGroup size='sm'>
          <InputGroup.Text>紙面テンプレートファイル(indtファイル)</InputGroup.Text>
          <Form.Control
            id='templateFilenameInput'
            type='text'
            name='templateFilename'
            defaultValue={page?.templateFilename || ''}
            readOnly
          />
        </InputGroup>
      </Col>
    </Row>
  )
}

const listItemClassName = 'py-1 px-2'

export const LinesInformation = ({
  layoutable
}: {
  layoutable: {
    bottomAdNumberOfColumns: number
    numberOfColumns: number
    numberOfRows: number
    articles?: Article[]
  }
}) => {
  const { bottomAdNumberOfColumns, numberOfColumns, numberOfRows } = layoutable
  const requiredLines = numberOfColumns * numberOfRows
  const bottomAdLines = bottomAdNumberOfColumns * numberOfRows
  const articleOccupyingLines =
    layoutable?.articles?.map((a) => getArticleOccupyingLines(a))?.reduce((prev, curr) => prev + curr, 0) || 0
  const existingLines = bottomAdLines + articleOccupyingLines
  const differenceLines = existingLines - requiredLines
  const differenceStr = differenceLines < 0 ? `アキ: ${Math.abs(differenceLines)}行` : `アフレ: ${differenceLines}行`
  const differenceClassName = differenceLines < 0 ? `${listItemClassName} text-danger fw-bold` : listItemClassName
  return (
    <ListGroup horizontal className='justify-content-end my-2'>
      <ListGroup.Item className={listItemClassName}>全体行数: {requiredLines}</ListGroup.Item>
      <ListGroup.Item className={listItemClassName}>出稿行数: {existingLines}</ListGroup.Item>
      <ListGroup.Item className={differenceClassName}>{differenceStr}</ListGroup.Item>
    </ListGroup>
  )
}
